import type {
	HelpCenterArticle,
	HelpCenterArticleProposal,
	HelpCenterArticleVersion,
	HelpCenterGroup,
	HelpCenterTab,
} from "@productlane/zero"

// Pure aggregator extracted from `useArticleSuggestions` so it can be unit-
// tested with fixtures, without React or Zero. The hook is a thin shell that
// reads the two `useQuery` results and forwards them here.

export type SuggestionKind = "CREATE" | "EDIT" | "DELETE"

// One linkable source rendered on the proposal card. Comes from an `Issue` row
// (kind: "issue"), a parsed PR/commit identifier (kind: "pr"/"commit", url built
// from `owner/repo#number`), or a source thread (kind: "thread", in-app link to
// the conversation).
export interface SuggestionSource {
	kind: "issue" | "pr" | "commit" | "thread"
	label: string
	url: string
}

export interface SuggestionProposal {
	id: string
	kind: SuggestionKind
	articleId: string | null
	// HTML the proposal's `proposedSteps` apply to. Sourced from the FK
	// `baseVersion.content` (proposal's anchor in the article version chain).
	// May be null for CREATE or for DELETE proposals where there's no diff.
	baseArticleContent: string | null
	proposedSteps: unknown[]
	proposedTitle: string | null
	proposedGroupId: string | null
	// The folder a CREATE files its article under, null while it loads or once
	// the folder is gone.
	proposedFolder: SuggestionFolder | null
	proposedIcon: string | null
	proposalGroupId: string | null
	// Title of the parent HelpCenterProposalGroup, when this proposal is part
	// of one. Used as the AGENT card's headline in the review rail.
	proposalGroupTitle: string | null
	// Agent-written 1-3 sentence rationale, set via the `set_description`
	// tool and stored on the parent group. Null for groups created before
	// this was wired up and for standalone USER proposals.
	description: string | null
	// The auto-merge reviewer's verdict, when it looked at this draft.
	agentReview: string | null
	// Linkable issues + PRs the agent run was triggered by. Empty when the
	// group has no provenance attached.
	sources: SuggestionSource[]
	createdAt: number
	authorType: "USER" | "AGENT"
	createdByUserId: string | null
	// DRAFT with nobody to finish it — written by the v2 API with
	// submit_for_review false. Shown here because no other screen reaches it.
	isOrphanDraft: boolean
}

export interface SuggestionFolder {
	id: string
	name: string
	// Null for a folder on the default tab.
	tabName: string | null
}

export interface SuggestionRow {
	// Stable key — articleId for existing-article rows, "new:<proposalId>" for CREATE.
	key: string
	// Dominant kind — DELETE wins over EDIT, CREATE rows are always CREATE.
	kind: SuggestionKind
	title: string
	// True when the suggestion targets an existing article (so EDIT/DELETE).
	articleId: string | null
	// All OPEN proposals that contribute to this row.
	proposals: SuggestionProposal[]
	// Every contributing proposal is an ownerless DRAFT, so the row is waiting
	// to be submitted rather than reviewed.
	isOrphanDraft: boolean
}

// Zero-typed row shape of an OPEN proposal from
// `openProposalsForWorkspace` / `openProposalGroupsForWorkspace.proposals` —
// the base table row plus the related `article` (for its title) and
// `baseVersion` (for its `content` HTML, used as the proposal's anchor).
export type RawProposal = HelpCenterArticleProposal & {
	article?: Pick<HelpCenterArticle, "title" | "isDeleted"> | null
	baseVersion?: Pick<HelpCenterArticleVersion, "content"> | null
	proposedGroup?:
		| (Pick<HelpCenterGroup, "id" | "name"> & {
				tab?: Pick<HelpCenterTab, "name"> | null
		  })
		| null
}

// Minimal issue shape used to render card sources. Pulled from the
// `helpCenterProposalGroups.issues` M2M on the Zero query.
export interface RawProposalGroupIssue {
	id: string
	identifier: string
	title: string
	url: string
}

// Minimal thread shape for card sources. Pulled from the
// `helpCenterProposalGroups.sourceThreads` M2M on the Zero query.
export interface RawProposalGroupThread {
	id: string
	title?: string | null
}

export interface RawProposalGroup {
	id: string
	title?: string | null
	// Agent-written rationale stored on the group. Surfaced as the
	// proposal card's description.
	description?: string | null
	proposals?: readonly RawProposal[]
	// Provenance — the issues + PR identifiers + source threads the agent run was
	// triggered by. Any may be undefined/empty for groups created before this was
	// wired up, or for non-agent flows.
	issues?: readonly RawProposalGroupIssue[]
	pullRequestIdentifiers?: readonly string[]
	sourceThreads?: readonly RawProposalGroupThread[]
}

export interface AggregatedSuggestions {
	rows: SuggestionRow[]
	rowsByKey: Map<string, SuggestionRow>
	rowByArticleId: Map<string, SuggestionRow>
	proposalById: Map<string, SuggestionProposal>
}

function normalize(
	raw: RawProposal,
	proposalGroupTitle: string | null = null,
	description: string | null = null,
	sources: SuggestionSource[] = [],
): SuggestionProposal {
	const baseContent = raw.baseVersion?.content
	return {
		id: raw.id,
		kind: raw.kind,
		articleId: raw.articleId ?? null,
		// `baseVersion.content` is `JSONValue` in the schema (legacy genesis
		// rows can hold non-string content), so narrow at the boundary.
		baseArticleContent: typeof baseContent === "string" ? baseContent : null,
		proposedSteps: Array.isArray(raw.proposedSteps) ? raw.proposedSteps : [],
		proposedTitle: raw.proposedTitle ?? null,
		proposedGroupId: raw.proposedGroupId ?? null,
		proposedFolder: raw.proposedGroup
			? {
					id: raw.proposedGroup.id,
					name: raw.proposedGroup.name,
					tabName: raw.proposedGroup.tab?.name ?? null,
				}
			: null,
		proposedIcon: raw.proposedIcon ?? null,
		proposalGroupId: raw.proposalGroupId ?? null,
		proposalGroupTitle,
		description,
		agentReview: raw.agentReview ?? null,
		sources,
		createdAt: raw.createdAt,
		authorType: raw.authorType,
		createdByUserId: raw.createdByUserId ?? null,
		isOrphanDraft: isOrphanDraft(raw),
	}
}

// The v2 API and MCP write a lone USER proposal with no author; the dashboard
// always stamps the teammate.
export function isApiProposal(
	p: Pick<
		SuggestionProposal,
		"authorType" | "createdByUserId" | "proposalGroupId"
	>,
): boolean {
	return p.authorType === "USER" && !p.createdByUserId && !p.proposalGroupId
}

export function isApiRow(row: Pick<SuggestionRow, "proposals">): boolean {
	return row.proposals.length > 0 && row.proposals.every(isApiProposal)
}

export function isOrphanDraft(raw: {
	status: string
	createdByUserId?: string | null
}): boolean {
	return raw.status === "DRAFT" && !raw.createdByUserId
}

// Build the per-source list rendered under the description on the card.
// Issues come straight from the M2M; PRs are parsed from the
// `owner/repo#number` identifier the agent persisted on the group, with the
// canonical GitHub URL constructed from the parts.
const PR_IDENT_REGEX = /^([^/]+)\/([^#]+)#(\d+)$/
const COMMIT_IDENT_REGEX = /^([^/]+)\/([^@]+)@([0-9a-f]+)$/
export function buildProposalGroupSources(
	issues: readonly RawProposalGroupIssue[] | undefined,
	pullRequestIdentifiers: readonly string[] | undefined,
	sourceThreads?: readonly RawProposalGroupThread[],
): SuggestionSource[] {
	const out: SuggestionSource[] = []
	for (const issue of issues ?? []) {
		out.push({
			kind: "issue",
			label: `${issue.identifier}: ${issue.title}`,
			url: issue.url,
		})
	}
	for (const thread of sourceThreads ?? []) {
		out.push({
			kind: "thread",
			label:
				thread.title && thread.title.length > 0 ? thread.title : "Conversation",
			url: `/threads/${thread.id}`,
		})
	}
	// Pull requests ahead of commits, so the chips a folded card shows first
	// are the ones that name the change.
	const commits: SuggestionSource[] = []
	for (const ident of pullRequestIdentifiers ?? []) {
		const prMatch = ident.match(PR_IDENT_REGEX)
		if (prMatch) {
			const [, owner, repo, number] = prMatch
			out.push({
				kind: "pr",
				label: ident,
				url: `https://github.com/${owner}/${repo}/pull/${number}`,
			})
			continue
		}
		const commitMatch = ident.match(COMMIT_IDENT_REGEX)
		if (commitMatch) {
			const [, owner, repo, sha] = commitMatch
			commits.push({
				kind: "commit",
				label: ident,
				url: `https://github.com/${owner}/${repo}/commit/${sha}`,
			})
		}
	}
	return [...out, ...commits]
}

// Aggregates standalone OPEN proposals + OPEN proposal groups (with their
// children) into one row per target article. Group boundaries are
// intentionally erased — the consumer reviews the merged view per article.
//
// Deterministic: row.proposals are sorted by `(createdAt, id)` and rows are
// sorted newest-first by their latest proposal's createdAt. Same input →
// same output across remounts.
export function aggregateProposals(args: {
	standalone: readonly RawProposal[]
	groups: readonly RawProposalGroup[]
}): AggregatedSuggestions {
	const { standalone, groups } = args
	const flat: { proposal: SuggestionProposal; articleTitle: string | null }[] =
		[]
	for (const p of standalone) {
		if (p.article?.isDeleted) continue
		flat.push({
			proposal: normalize(p),
			articleTitle: p.article?.title || null,
		})
	}
	for (const group of groups) {
		const groupTitle =
			group.title && group.title.length > 0 ? group.title : null
		const description =
			group.description && group.description.length > 0
				? group.description
				: null
		const sources = buildProposalGroupSources(
			group.issues,
			group.pullRequestIdentifiers,
			group.sourceThreads,
		)
		for (const child of group.proposals ?? []) {
			if (child.status !== "OPEN" && !isOrphanDraft(child)) continue
			if (child.article?.isDeleted) continue
			flat.push({
				proposal: normalize(child, groupTitle, description, sources),
				articleTitle: child.article?.title || null,
			})
		}
	}

	const byKey = new Map<string, SuggestionRow>()
	for (const { proposal, articleTitle } of flat) {
		const targetKey = proposal.articleId ?? `new:${proposal.id}`
		let row = byKey.get(targetKey)
		if (!row) {
			row = {
				key: targetKey,
				kind: proposal.kind,
				title:
					articleTitle ??
					(proposal.kind === "CREATE" || proposal.proposedTitle
						? proposal.proposedTitle || "Untitled"
						: "Article"),
				articleId: proposal.articleId,
				proposals: [],
				isOrphanDraft: true,
			}
			byKey.set(targetKey, row)
		}
		row.proposals.push(proposal)
		if (!proposal.isOrphanDraft) row.isOrphanDraft = false
		// DELETE wins over EDIT for the row's icon.
		if (proposal.kind === "DELETE") {
			row.kind = "DELETE"
		} else if (row.kind !== "DELETE" && proposal.kind === "EDIT") {
			row.kind = "EDIT"
		}
	}

	for (const row of byKey.values()) {
		row.proposals.sort(
			(a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id),
		)
	}

	const rows = [...byKey.values()].sort((a, b) => {
		const aLatest = Math.max(...a.proposals.map((p) => p.createdAt))
		const bLatest = Math.max(...b.proposals.map((p) => p.createdAt))
		return bLatest - aLatest
	})

	const rowByArticleId = new Map<string, SuggestionRow>()
	const proposalById = new Map<string, SuggestionProposal>()
	for (const row of rows) {
		if (row.articleId) rowByArticleId.set(row.articleId, row)
		for (const p of row.proposals) proposalById.set(p.id, p)
	}

	return { rows, rowsByKey: byKey, rowByArticleId, proposalById }
}
